// Per-browser risk configuration. Lives in localStorage so each user can
// configure their own caps without coordinating with the executor — these
// are client-enforced for now; PR 11 will add a server-side mirror so a
// tampered browser still can't bypass the caps.
//
// All money values are in *cents* to match Kalshi's native unit. Notional
// is computed as `price (0-1) * size (contracts) * 100` — i.e. the worst-
// case downside if the contract settles against you.

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'ladderbook.risk.v1';

export interface RiskConfig {
  // Hard ceiling — orders above this notional are rejected client-side
  // with no modal escape hatch. Lift it in /profile if you really need to.
  maxOrderNotionalCents: number;
  // Soft ceiling — orders above this notional but below the hard cap pop a
  // confirm modal before submit.
  confirmAboveCents: number;
  // Quick-trade default size (contracts) — used when the user clicks a
  // price cell on the game page.
  quickTradeSize: number;
  // When true, all submits are blocked locally. Toggled from the layout
  // header by the trader; reflects intent ("stop trading") without
  // affecting the vault state.
  paused: boolean;
}

const DEFAULTS: RiskConfig = {
  maxOrderNotionalCents: 25_00, // $25
  confirmAboveCents: 10_00, // $10
  quickTradeSize: 1,
  paused: false,
};

let current: RiskConfig = DEFAULTS;
let loaded = false;
const listeners = new Set<() => void>();

function load(): void {
  if (loaded) return;
  loaded = true;
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<RiskConfig>;
    current = {
      maxOrderNotionalCents: clampPositive(parsed.maxOrderNotionalCents) ?? DEFAULTS.maxOrderNotionalCents,
      confirmAboveCents: clampPositive(parsed.confirmAboveCents) ?? DEFAULTS.confirmAboveCents,
      quickTradeSize: clampPositive(parsed.quickTradeSize) ?? DEFAULTS.quickTradeSize,
      paused: parsed.paused === true,
    };
  } catch {
    current = DEFAULTS;
  }
}

function clampPositive(n: unknown): number | null {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  return n;
}

function persist(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
}

export function getRiskConfig(): RiskConfig {
  load();
  return current;
}

export function updateRiskConfig(patch: Partial<RiskConfig>): void {
  load();
  current = {
    ...current,
    ...(patch.maxOrderNotionalCents != null
      ? { maxOrderNotionalCents: Math.max(1, patch.maxOrderNotionalCents) }
      : {}),
    ...(patch.confirmAboveCents != null
      ? { confirmAboveCents: Math.max(0, patch.confirmAboveCents) }
      : {}),
    ...(patch.quickTradeSize != null
      ? { quickTradeSize: Math.max(1, Math.floor(patch.quickTradeSize)) }
      : {}),
    ...(patch.paused != null ? { paused: patch.paused } : {}),
  };
  persist();
  for (const l of listeners) l();
}

export function togglePause(): void {
  updateRiskConfig({ paused: !getRiskConfig().paused });
}

export function useRiskConfig(): RiskConfig {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => {
      load();
      return current;
    },
    () => DEFAULTS,
  );
}

// Returns the worst-case notional cost of an order in cents. For both buy
// and sell sides we use `price * size * 100` — that's the capital required
// to back the position. PR 11 will refine for partial-fill expectations.
export function orderNotionalCents(price: number, size: number): number {
  return Math.round(price * size * 100);
}

export type RiskDecision =
  | { ok: true }
  | { ok: false; reason: 'paused' | 'over-cap'; cents: number; capCents: number }
  | { ok: true; needsConfirm: true; cents: number; threshold: number };

export function assessOrder(price: number, size: number): RiskDecision {
  const cfg = getRiskConfig();
  if (cfg.paused) {
    return { ok: false, reason: 'paused', cents: 0, capCents: 0 };
  }
  const cents = orderNotionalCents(price, size);
  if (cents > cfg.maxOrderNotionalCents) {
    return { ok: false, reason: 'over-cap', cents, capCents: cfg.maxOrderNotionalCents };
  }
  if (cents > cfg.confirmAboveCents) {
    return { ok: true, needsConfirm: true, cents, threshold: cfg.confirmAboveCents };
  }
  return { ok: true };
}
